/**
 * @author Muhammad Hamza Khan
 * @datetime April, 18th 2022
 * @description: This is Edit Floor Unit Component that is responsible for edit Unit from floors.
 */
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { Subscription, Subject } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { PageName } from 'src/app/shared/utils/user-roles';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { FormControl, FormGroup } from '@angular/forms';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { LoaderService } from 'src/app/shared/utils/loader.service';
import {
  IselectedUnit,
  IselectedFloor,
  IselectedBuilding,
  IselectedSite,
} from 'src/app/shared/models/data-interfaces';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { unitStatusRequest } from 'src/app/shared/constants/request-mapping';
import { TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-edit-floor-unit',
  templateUrl: './edit-floor-unit.component.html',
  styleUrls: ['./edit-floor-unit.component.scss'],
})
export class EditFloorUnitComponent implements OnInit, OnDestroy {
  public ngDestroyed$ = new Subject();
  @Input() public selectedSite: IselectedSite | undefined;
  @Input() selectedBuilding: IselectedBuilding | undefined;
  @Input() public selectedFloor: IselectedFloor | undefined;
  @Input() selectedUnit: IselectedUnit | undefined;
  @Input() public enableBackOperation!: boolean | true;
  @Input() navigateConfirmDialog: any;
  @Input() closeConfirmDialog: any;
  @Input() public resetNavigation: any;
  previousUnitName: string = '';
  editUnitForm: any;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  PageName = PageName;
  sharedData!: any;
  floorManagemetSubscription: Subscription | undefined;
  unitList: any = [];
  unit: any = {};
  floorPrefix: string = '';

  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private sharedDataService: SharedDataService,
    private floorManagementService: FloorManagementService,
    private helperService: HelperService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private translate: TranslateService
  ) {
    this.sharedDataService.currentData.subscribe((sharedData: any) => {
      this.sharedData = sharedData;
    });
  }
  /**
   * @description: runs on when component is initialized.
   * @input {none}
   * @output {void}
   */

  ngOnInit(): void {
    this.editUnitForm = new FormGroup({
      unitName: new FormControl(null, [
        MFValidators.required,
        MFValidators.minLength(1),
        MFValidators.maxLength(50),
        MFValidators.whitespace,
        MFValidators.requiredSpecialCharForUnitName,
      ]),
    });
    this.editUnitForm.patchValue({
      unitName: this.sharedData?.selectedUnit?.unitName?.replace(
        this.floorPrefix,
        ''
      ),
    });
    this.previousUnitName = this.sharedData?.selectedUnit?.unitName;
    this.editUnitForm?.controls?.unitName?.markAsTouched();

    this.floorManagemetSubscription = this.floorManagementService
      .getUnitsAndAccessPointsByFloor(
        this.selectedSite?.siteId,
        this.selectedBuilding?.buildingId,
        this.selectedFloor?.floorId
      )
      .subscribe({
        next: (res: any) => {
          this.unitList = res;
          this.unit = this.unitList?.find(
            (unit: any) => unit?.unitId == this.sharedData.selectedUnit.unitId
          );

          //updating unit name
          this.floorPrefix = this.unit?.unitPrefix;
          this.editUnitForm.patchValue({
            unitName: this.sharedData?.selectedUnit?.unitName?.replace(
              this.floorPrefix,
              ''
            ),
          });
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });
  }

  /**
   * @description: closeDialog function close the dialogRef.
   * @input none
   * @output { void }
   */

  closeDialog() {
    let unitName: string = this.editUnitForm?.controls?.unitName?.value;

    let currentUnitName = this.previousUnitName;

    if (unitName === currentUnitName) {
      this.resetNavigation();
      this.dialogRef?.close();
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.resetNavigation();
        this.dialogRef?.close();
      }
    });
  }

  /**
   * @description: navigateBack function close the dialogRef.
   * @input none
   * @output { void }
   */
  navigateBack() {
    this.dialogRef?.close();
  }

  /**
   * @description editUnitHandler function accepts siteId , buildingId , floorId , unitId and call the updateUnit API from floorManagementService.
   * @param { string } siteId
   * @param { string } buildingId
   * @param { string } floorId
   * @param { string } unitId
   * @param { string } unitName
   * @param { string } unitStatus
   * @returns
   */
  editUnitHandler(
    siteId: string,
    buildingId: string,
    floorId: string,
    unitId: string,
    unitName: string,
    unitStatus: string
  ): void {
    unitName = unitName.toString();

    if (this.floorPrefix + unitName != this.sharedData.selectedUnit.unitName) {
      if (unitStatus === CONSTANTS.PENDING_DELETE) {
        this.notificationService.showError(
          this.translate.instant(
            'UNIT_IS_IN_PENDING_DELETE_STATE_AND_CAN_NOT_BE_UPDATED'
          )
        );
        return;
      }

      this.floorManagementService
        .updateUnit(
          siteId,
          buildingId,
          floorId,
          unitId,
          unitName,
          unitStatusRequest[this.translate.instant(unitStatus)]
        )
        .subscribe({
          next: (res: any) => {
            if (res?.unitId === this?.sharedData?.selectedUnit?.unitId) {
              this.sharedDataService.setSelectedUnit({
                unitId: unitId,
                unitName: this.floorPrefix + unitName,
              });
              this.floorManagementService.setTriggerRerender(true);
              this.dialogRef?.close();
              this.loaderService.show();

              this.notificationService.showSuccess(
                this.translate.instant('SUCCESSFULLY_UPDATED')
              );
            }
          },
          error: (err: Error) => {
            this.notificationService.showError(err?.message);
          },
        });
    } else this.dialogRef?.close();
  }

  /**
   * @description: runs just before component unmount. Unsubscribes the sharedDataService service
   * @input {none}
   * @output {void}
   */

  ngOnDestroy(): void {
    this.floorManagemetSubscription?.unsubscribe();
  }
}
